import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { ILink } from './DefaultToastLayout.types';

interface Props {
	className?: string;
	title: string | React.ReactNode;
	message: string | React.ReactNode;
	link?: ILink;
	onClose?: () => void;
	type: 'info' | 'warning' | 'success' | 'error';
}

const DefaultToastLayout = ({ className, title, onClose, message, link, type }: Props) => {
	const iconVariant = useMemo(() => {
		switch (type) {
			case 'warning':
				return 'exclamation-circle';
			case 'success':
				return 'checkmark-circle';
			case 'error':
				return 'cross-circle';
			default:
				return 'i-circle';
		}
	}, [type]);

	return (
		<div className={className}>
			<Icon variant={iconVariant} />
			<ContentWrapper>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.DEFAULT_TOAST_TITLE}>
					<Text variant="p-lg" bold>
						{title}
					</Text>
				</UIElementIdentifier>

				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.DEFAULT_TOAST_MESSAGE}>
					<Text variant="p-md">{message}</Text>
					{link ? (
						<Link onClick={onClose} to={link.location}>
							<Text variant="p-md">{link.text}</Text>
						</Link>
					) : null}
				</UIElementIdentifier>
			</ContentWrapper>
		</div>
	);
};

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const Memoized = memo(DefaultToastLayout);
const Styled = styled(Memoized)`
	display: flex;
	align-items: center;
	justify-content: flex-start;

	${ContentWrapper} {
		margin-left: 1rem;
	}
	${Icon} {
		&.svg {
			--size: 1.4rem;
		}
	}
	${Text} {
		color: ${({ theme }) => theme.v2.color.neutral[1100]};
	}
	a > ${Text} {
		color: ${({ theme }) => theme.v2.color.secondary[800]};
	}
`;

export { Styled as DefaultToastLayout };
